import type { AssessmentComponent } from "@pandas/http-client";
import { IconInfoCircle } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AssessmentGuide from "@/components/Assessment/AssessmentGuide";
import AssessmentRow from "@/components/Assessment/AssessmentRow";
import GradingInstructions from "@/components/Assessment/GradingInstructions";
import Button from "@/components/core/Buttons/Button";
import Loader from "@/components/core/Loaders/Loader";
import { PopUp } from "@/components/core/Modals/PopUp";
import { useAssessmentSubmit } from "@/hooks/useAssessmentSubmit";
import { useTagManager } from "@/hooks/useTagManager";
import { requireAuthWithDevice } from "@/lib/guards";
import { platformApi } from "@/services";
import { useStore } from "@/store";
import { getSortedAssessmentKeys } from "@/utils/assessment/display";

export const Route = createFileRoute("/$locale/$partner/conditions")({
	beforeLoad: requireAuthWithDevice,
	component: Conditions,
});

function Conditions() {
	const { t } = useTranslation();
	const [openModal, setOpenModal] = useState(false);
	const [clickedComponent, setClickedComponent] = useState<AssessmentComponent | undefined>();
	const [openInfoModal, setOpenInfoModal] = useState(false);
	const assessmentResults = useStore((state) => state.assessmentResults);
	const setAssessmentResults = useStore((state) => state.setAssessmentResults);
	const category = useStore((state) => state.category);
	const assessmentsComponentVersion = useStore((state) => state.config?.partnerSettings.componentVersion);

	useTagManager("Step Conditions");

	const { data: supportedAssessments, isLoading } = useQuery({
		queryKey: [
			"supported-assessments",
			{
				category,
				assessmentsComponentVersion: assessmentsComponentVersion || 2,
			},
		],
		queryFn: () =>
			platformApi.getSupportedAssessments(String(assessmentsComponentVersion || "2"), category.toLowerCase()),
		staleTime: 3600000,
	});

	const assessmentsToDisplay = useMemo(() => {
		if (supportedAssessments) {
			return getSortedAssessmentKeys(supportedAssessments);
		}
		return [];
	}, [supportedAssessments]);

	const allAssessmentsCompleted =
		!!supportedAssessments && assessmentsToDisplay.length <= Object.keys(assessmentResults).length;

	const { loading, submitDisabled, error, handleSubmit } = useAssessmentSubmit({ supportedAssessments });
	const disabled = !allAssessmentsCompleted || submitDisabled;

	if (isLoading)
		return (
			<div className="absolute w-fit h-fit top-1/3 left-1/2">
				<Loader />
			</div>
		);

	return (
		<>
			<div className="justify-center max-w-[80rem] flex flex-col items-center m-auto relative">
				<div className="text-center mt-7">
					<div className="flex items-center max-w-2xl leading-7">
						<p className="text-gray pr-1">
							{t(`OpticalCheck.Last checks to calculate a final offer`)}
							<span
								onClick={() => setOpenInfoModal(true)}
								className="cursor-pointer"
							>
								<IconInfoCircle
									width={24}
									height={24}
									stroke={1}
									color="grey"
									className="ml-1 inline-block"
								/>
							</span>
						</p>
					</div>
				</div>
				<div className="m-auto mt-8 px-7 lg:px-14 max-w-[69rem] w-full">
					{supportedAssessments &&
						assessmentsToDisplay.map((assessment) =>
							supportedAssessments[assessment]?.components.map((component: AssessmentComponent) => (
								<AssessmentRow
									key={component.id}
									component={component}
									assessment={assessment}
									supportedAssessments={supportedAssessments}
									assessmentResults={assessmentResults}
									onSelect={setAssessmentResults}
									onInfoClick={(comp) => {
										setOpenModal(true);
										setClickedComponent(comp);
									}}
								/>
							)),
						)}
				</div>
				<div className="mt-24 mb-24 mx-auto">
					{error && <p className="text-center pb-3 text-red">{error}</p>}
					<Button
						loading={loading}
						disabled={disabled}
						label={t("Buttons.See Report")}
						onClick={handleSubmit}
					/>
				</div>
			</div>
			{clickedComponent && (
				<PopUp
					className="max-w-[70rem]"
					isOpen={openModal}
					setIsOpen={setOpenModal}
				>
					<AssessmentGuide clickedComponent={clickedComponent} />
				</PopUp>
			)}
			<PopUp
				className="w-[48rem] py-5 px-16"
				isOpen={openInfoModal}
				setIsOpen={setOpenInfoModal}
			>
				<GradingInstructions />
			</PopUp>
		</>
	);
}
